import React, { useState, useEffect, useRef } from 'react'
import { renderField } from '../utils'
import {
  ANALYSIS_MODE,
  CHART_DEFINITION_FIELDS
} from '../../chartConfigurationsV2'
import { NumberInput } from './NumberInput'
import { THEME_COLOR_SWATCH } from 'utils/constants'
import Modal from 'components/Modal';
import InfoBox from 'components/InfoBox';
import { VuiAccordion } from '@topkamera/viso-ui';

// Compute alternating indices over a palette of given length
// Order: 0, last, 1, last-1, 2, last-2, ...
function getAlternatingIndex(position, length) {
  const half = Math.floor(position / 2)
  if (position % 2 === 0) {
    return half % length
  }
  return ((length - 1) - half) % length
}

export const DataStreams = ({
  value = 1,
  streams = [],
  onChange = () => { },
  selectedTableName = '',
  metricData = [],
  workspace,
  chartValues,
  allChartValues,
  maxDataStreams = null, // Restrict maximum number of datastreams
  hideAnalysisMode = false, // Hide analysis mode for raw data mode
  color = '',
  onChangeField = () => { }
}) => {
  const [showConfirm, setShowConfirm] = useState(false)
  const [pendingOption, setPendingOption] = useState(null)
  const [pendingIndex, setPendingIndex] = useState(null)
  const [currentStreams, setCurrentStreams] = useState([...streams])
  const [lastTrigger, setLastTrigger] = useState(null)
  const [streamBefore, setStreamBefore] = useState(null)
  const [intervalBefore, setIntervalBefore] = useState(null)
  const lastIntervalRef = useRef(chartValues?.analysisInterval)

  // keep local mirror in sync when parent prop changes
  // Auto-limit to first stream only when there are multiple group by fields
  useEffect(() => {
    const hasMultipleGroupBy = chartValues?.groupBy?.metrics?.length > 1

    if (hasMultipleGroupBy && streams.length > 1) {
      // Keep only the first stream when multiple group by fields exist
      const firstStreamOnly = streams
        .map(item => ({ ...item, streamLabel: { ...item.streamLabel, value: '' } }))
        .filter((item, index) => index === 0)

      setCurrentStreams(firstStreamOnly)
      const hasChanged = streams.length > 1 || (streams[0]?.streamLabel?.value !== '')
      if (hasChanged) {
        onChange({ value: 1, streams: firstStreamOnly })
      }
    } else {
      setCurrentStreams([...streams])
    }
  }, [streams, chartValues?.groupBy?.metrics])

  // When analysisInterval changes, ensure compatibility with certain analysis modes
  useEffect(() => {
    const requiresDaysIdx = currentStreams.findIndex(s => s?.analysisMode === 'peakHourOfDay' || s?.analysisMode === 'rollingNDayAverage')

    // Detect actual interval change and record previous value for potential revert
    const prevInterval = lastIntervalRef.current
    const currInterval = chartValues?.analysisInterval
    const intervalChanged = !!prevInterval && !!currInterval && (
      prevInterval.quantity !== currInterval.quantity || prevInterval.value !== currInterval.value
    )
    if (intervalChanged) {
      setLastTrigger('interval')
      setIntervalBefore(prevInterval)
    }

    if (requiresDaysIdx !== -1) {
      const isDays = chartValues?.analysisInterval?.quantity === 1 && chartValues?.analysisInterval?.value === 'Days'
      if (!isDays && !showConfirm) {
        const offender = currentStreams[requiresDaysIdx] || null
        setPendingOption(offender)
        setPendingIndex(requiresDaysIdx)
        setShowConfirm(true)
      }
    }
    // Update last interval ref after handling
    lastIntervalRef.current = currInterval
  }, [chartValues?.analysisInterval?.quantity, chartValues?.analysisInterval?.value, currentStreams, showConfirm])

  const handleInputChange = (value) => {
    const hasMultipleGroupBy = chartValues?.groupBy?.metrics?.length > 1

    // Disable data stream value changes only when there are multiple group by fields
    if (hasMultipleGroupBy) {
      return
    }

    // Original logic for when there's single or no group by
    if (value === '') {
      return
    }

    const parsed = parseInt(value, 10)
    if (!isNaN(parsed) && parsed >= 0) {
      // Enforce maxDataStreams limit if specified
      const finalValue = maxDataStreams ? Math.min(parsed, maxDataStreams) : parsed

      const newStreams = [...streams.slice(0, finalValue)]
      while (newStreams.length < finalValue) {
        // Assign color from theme swatch in alternating order: first, last, second, second-last, ...
        const colorIndex = getAlternatingIndex(newStreams.length, THEME_COLOR_SWATCH.length)
        const assignedColor = THEME_COLOR_SWATCH[colorIndex]

        const newStream = {
          ...ANALYSIS_MODE.VALUES,
          streamLabel: {
            ...ANALYSIS_MODE.VALUES.streamLabel,
            color: assignedColor
          }
        }
        newStreams.push(newStream)
      }
      setCurrentStreams(newStreams)
      onChange({ value: parsed, streams: newStreams })
    }
  }

  const handleBlur = () => {
    if (value === '' || value < 1) {
      const defaultValue = 1
      const defaultStreams = [currentStreams[0] || { ...ANALYSIS_MODE.VALUES }]

      onChange({ value: defaultValue, streams: defaultStreams })
    }
  }

  const handleStreamChange = (index, updatedStream) => {
    const updatedStreams = [...streams]
    updatedStreams[index] = updatedStream
    setCurrentStreams(updatedStreams)

    if (updatedStream?.analysisMode === 'peakHourOfDay' || updatedStream?.analysisMode === 'rollingNDayAverage') {
      if (chartValues?.analysisInterval?.quantity !== 1 || chartValues?.analysisInterval?.value !== 'Days') {
        setLastTrigger('stream')
        setStreamBefore(streams[index])
        setPendingOption(updatedStream)
        setPendingIndex(index)
        setShowConfirm(true)
      }
    } else {
      onChange({ value: value, streams: updatedStreams })
    }
  }

  const handleConfirm = () => {
    // change chartValues.analysisInterval to updatedAnalysisInterval
    const updatedAnalysisInterval = {
      quantity: 1,
      value: 'Days'
    }
    // apply the pending stream change
    if (pendingOption != null && pendingIndex != null) {
      const updatedStreams = [...streams]
      updatedStreams[pendingIndex] = {
        ...updatedStreams[pendingIndex],
        ...pendingOption,
      }
      setCurrentStreams(updatedStreams)
      onChange({ value, streams: updatedStreams })
    }
    // update chart-level analysis interval
    onChangeField('analysisInterval', updatedAnalysisInterval)
    setShowConfirm(false)
    setPendingOption(null)
    setPendingIndex(null)
  }

  const handleCancel = () => {
    setShowConfirm(false)
    // Revert based on what triggered the modal
    if (lastTrigger === 'stream' && pendingIndex != null && streamBefore) {
      const reverted = [...currentStreams]
      reverted[pendingIndex] = streamBefore
      setCurrentStreams(reverted)
      // also propagate the revert so child selects receive updated props and re-select the correct option
      onChange({ value, streams: reverted })
    }
    if (lastTrigger === 'interval' && intervalBefore) {
      onChangeField('analysisInterval', intervalBefore)
    }
    setPendingOption(null)
    setPendingIndex(null)
    setLastTrigger(null)
    setStreamBefore(null)
    setIntervalBefore(null)
  }

  const ConfirmModeChangeModal = ({ open, onConfirm, onCancel }) => {
    if (!open) return null;

    const modeLabel = pendingOption ? (ANALYSIS_MODE[pendingOption.analysisMode]?.label || pendingOption.analysisMode) : ''

    const buttons = [
      <button className="modal__confirm" key="confirm" onClick={onConfirm}>
        Confirm
      </button>,
      <button className="modal__cancel" key="reset" onClick={onCancel}>
        Cancel
      </button>,
    ];

    return (
      <Modal
        buttons={buttons}
        classNames='modal__frame_sm'
        title='Analysis Mode'
        body={
          <div>
            <div style={{ marginBottom: 20 }}>The <span className='viso-text-accent2'>{modeLabel}</span> analysis mode requires suitable settings. Confirm to apply them:</div>
            <InfoBox>
              <ul>
                <li><strong>Time interval:</strong> 1</li>
                <li><strong>Time range:</strong> Days</li>
              </ul>
            </InfoBox>
          </div>
        }
        onClose={onCancel}
      />
    )
  }

  const hasMultipleGroupBy = chartValues?.groupBy?.metrics?.length > 1

  return (
    <div className='chart-editor-data-streams'>
      <NumberInput
        label='Data Stream'
        min={1}
        max={hasMultipleGroupBy ? 1 : (maxDataStreams || undefined)}
        step={1}
        placeholder=''
        value={hasMultipleGroupBy ? 1 : value}
        onChange={handleInputChange}
        onBlur={handleBlur}
        id='data-streams'
        readOnly={hasMultipleGroupBy}
        disabled={hasMultipleGroupBy}
      />

      <div className='chart-editor-data-streams__stream'>
        <VuiAccordion
          style='section'
          defaultOpenIndex={0}
          alwaysOpen
          items={currentStreams.map((stream, index) => {
            return ({
              title: <div>
                Stream {index + 1} {currentStreams[index].streamLabel.value ? '- ' : ''}
                <span className='vui-text-text'>{currentStreams[index].streamLabel.value}</span>
              </div>,
              content: (
                <Stream
                  key={`${index}_${stream?.metric?.metrics?.length}`}
                  index={index}
                  streamValues={stream}
                  chartValues={chartValues}
                  allChartValues={allChartValues}
                  selectedTableName={selectedTableName}
                  metricData={metricData}
                  onChange={(updated) => handleStreamChange(index, updated)}
                  workspace={workspace}
                  hideAnalysisMode={hideAnalysisMode}
                  color={color}
                />
              )
            })
          })}
        />
      </div>
      <ConfirmModeChangeModal open={showConfirm} onConfirm={handleConfirm} onCancel={handleCancel} />
    </div>
  )
}

export const Stream = (props = {
  index: null,
  streamValues: null,
  selectedTableName: '',
  metricData: [],
  onChange: () => null,
  workspace,
  chartValues,
  allChartValues,
  hideAnalysisMode: false,
  color: ''
}) => {
  const {
    streamValues, onChange, chartValues, hideAnalysisMode, allChartValues
  } = props
  const [currentStreamValues, setCurrentStreamValues] = useState({ ...streamValues })
  useEffect(() => {
    setCurrentStreamValues({ ...streamValues })
  }, [streamValues])

  // Conditionally include analysisMode based on hideAnalysisMode prop
  const baseFields = hideAnalysisMode ? ['streamLabel'] : ['streamLabel', 'analysisMode']
  const analysisFields = (ANALYSIS_MODE[currentStreamValues.analysisMode]?.fields || [])
  let fields = [...baseFields, ...analysisFields, 'enableTrendline']

  if (chartValues?.chartType === 'table') {
    // For table charts, expose numeric format, prefix/suffix, and per-stream
    // threshold controls (allThresholdType, coloring, colorTiers). These drive
    // cell coloring in the table body, but are independent from the base
    // stream color used in non-table charts.
    fields = [...baseFields, ...analysisFields, 'format', 'decimal', 'prefix', 'suffix', 'allThresholdType', 'coloring', 'colorTiers']
  }
  // Ensure no duplicate field ids (e.g., 'colorTiers' possibly injected via other toggles)
  fields = Array.from(new Set(fields))

  const handleFieldChange = (fieldId, value) => {
    const updatedStreamValues = {
      ...currentStreamValues,
      [fieldId]: value
    }

    setCurrentStreamValues(updatedStreamValues)
    onChange(updatedStreamValues)
  }

  return (
    <div className='viso-collapsible-menu border-left'>
      {fields.map((fieldId, index) => {
        return renderField(fieldId, CHART_DEFINITION_FIELDS, [], { ...allChartValues, ...currentStreamValues }, {
          ...props,
          onChangeField: handleFieldChange
        })
      })}
    </div>
  )
}
